import { Box } from "@mui/material";
import React, { useState } from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { formatWeekdayShortDate } from "../../../../../../../common/utils/date";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import { formatClockTime } from "../../helpers/activitySelectors";
import { ScheduledService } from "../../model/workoutPlanTypes";
import { DetailWidget, DetailWidgetGrid } from "../shared/DetailWidget";

type Props = {
  service: ScheduledService;
  date: string;
};

const HERO_HEIGHT = 180;

/**
 * A coached session's details, after the mobile `ServiceDetailsScreen`: the
 * service's image, type name and schedule, then the protocol tiles the
 * service carries (Duration always; Dose, Cycling Power, Altitude and
 * Intensity when set), its description and benefits.
 */
const ServiceContent = ({ service, date }: Props) => {
  const theme: Theme = useTheme();
  const [imageFailed, setImageFailed] = useState(false);

  const widgets: Array<{ title: string; value: string }> = [
    { title: "Duration", value: `${service.duration} min` },
  ];
  if (service.dose) widgets.push({ title: "Dose", value: String(service.dose) });
  if (service.cycling_power) {
    widgets.push({ title: "Cycling Power", value: `${service.cycling_power} watts` });
  }
  if (service.altitude) widgets.push({ title: "Altitude", value: `${service.altitude} m` });
  if (service.intensity != null) {
    widgets.push({ title: "Intensity", value: String(service.intensity) });
  }

  return (
    <>
      {service.image_url && !imageFailed && (
        <Box
          component="img"
          src={service.image_url}
          alt=""
          onError={() => setImageFailed(true)}
          sx={styles.hero}
        />
      )}
      <Box sx={styles.titleBlock}>
        <Box sx={styles.date(theme)}>
          {formatWeekdayShortDate(date)} · {formatClockTime(service.startTime)}
          {" - "}
          {formatClockTime(service.endTime)}
        </Box>
        <Box sx={styles.title(theme)}>{service.service_type_name}</Box>
      </Box>
      <DetailWidgetGrid>
        {widgets.map((widget) => (
          <DetailWidget key={widget.title} title={widget.title} value={widget.value} />
        ))}
      </DetailWidgetGrid>
      {service.description && (
        <Box sx={styles.section}>
          <Box sx={styles.sectionTitle(theme)}>Description</Box>
          <Box sx={styles.text(theme)}>{service.description}</Box>
        </Box>
      )}
      {service.benefits?.length > 0 && (
        <Box sx={styles.section}>
          <Box sx={styles.sectionTitle(theme)}>Benefits</Box>
          <Box component="ul" sx={styles.list(theme)}>
            {service.benefits.map((benefit) => (
              <li key={benefit}>{benefit}</li>
            ))}
          </Box>
        </Box>
      )}
    </>
  );
};

export default ServiceContent;

const styles = {
  hero: {
    display: "block",
    width: "100%",
    height: `${HERO_HEIGHT}px`,
    borderRadius: "8px",
    objectFit: "cover" as "cover",
  },
  titleBlock: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Bold)",
    fontSize: "20px",
    lineHeight: "28px",
  }),
  date: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  section: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  sectionTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  text: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
    whiteSpace: "pre-wrap" as "pre-wrap",
  }),
  list: (theme: Theme) => ({
    margin: 0,
    paddingLeft: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
};
